import { useEffect, useState } from "react";
import { MessageCircle, X } from "lucide-react";

// WhatsApp business number (India HQ) — international format, digits only
const WA_NUMBER = "918977109231";
const WA_PREFILL =
  "Hello VRVR Import & Exports, I'd like to discuss an import/export inquiry.";

export default function WhatsAppButton() {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(t);
  }, []);

  const href = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_PREFILL)}`;

  return (
    <div
      data-testid="whatsapp-widget"
      className={`fixed z-[60] bottom-5 right-5 sm:bottom-7 sm:right-7 flex flex-col items-end gap-3 transition-all duration-500 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
    >
      {open && (
        <div
          data-testid="whatsapp-card"
          className="w-[300px] rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden animate-[fadeIn_.25s_ease-out]"
        >
          <div className="relative bg-[#0E2A3F] text-white px-5 py-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366]">
                <WhatsAppGlyph className="h-5 w-5 text-white" />
              </span>
              <div className="leading-tight">
                <div className="text-xs uppercase tracking-[0.18em] text-[#F26F21] font-semibold">
                  Chat with us
                </div>
                <div className="font-[Outfit] font-semibold text-sm">
                  VRVR · Sales Team
                </div>
              </div>
            </div>
            <button
              data-testid="whatsapp-close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="absolute top-3 right-3 h-7 w-7 inline-flex items-center justify-center rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="px-5 py-5 bg-[#F8FAFC]">
            <div className="relative max-w-[240px] rounded-xl rounded-tl-sm bg-white border border-slate-200 px-4 py-3 text-sm text-[#0E2A3F] shadow-sm">
              Hi there 👋
              <br />
              How can we help with your shipment today?
              <div className="mt-1 text-[10px] text-slate-400">Typically replies within minutes</div>
            </div>
            <a
              data-testid="whatsapp-start-chat"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#25D366] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1ebe57] transition-colors"
            >
              <WhatsAppGlyph className="h-4 w-4" />
              Start chat on WhatsApp
            </a>
            <div className="mt-3 text-center text-[11px] text-slate-500">
              +91 8977 109 231 · Mon–Fri · 9 AM–6 PM IST
            </div>
          </div>
        </div>
      )}

      <button
        data-testid="whatsapp-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close WhatsApp chat" : "Open WhatsApp chat"}
        className="group relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#25D366] text-white shadow-[0_10px_30px_-5px_rgba(37,211,102,0.55)] hover:scale-105 hover:bg-[#1ebe57] transition-all"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping-slow pointer-events-none" />
        {open ? (
          <X className="relative h-6 w-6" />
        ) : (
          <WhatsAppGlyph className="relative h-7 w-7" />
        )}
        {!open && (
          <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-[#F26F21] text-[10px] font-bold text-white flex items-center justify-center ring-2 ring-white">
            1
          </span>
        )}
      </button>
    </div>
  );
}

function WhatsAppGlyph({ className = "" }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M19.11 17.205c-.372 0-1.087 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.045 2.293v.13c-.014.832.302 1.564.62 2.265 1.13 2.495 2.795 4.42 5.235 5.74.688.37 2.494 1.13 3.34 1.13.688 0 2.005-.36 2.62-1.005.39-.4.55-.952.55-1.49 0-.137-.043-.288-.072-.4-.158-.358-2.96-1.595-3.31-1.595zM16 7.227c-4.846 0-8.773 3.927-8.773 8.773 0 1.85.575 3.6 1.55 5.04L7.158 24.85l3.92-1.26c1.39.937 3.06 1.434 4.92 1.434 4.846 0 8.773-3.927 8.773-8.773S20.846 7.227 16 7.227zm0 16.142c-1.59 0-3.05-.452-4.296-1.236l-3.06.985.998-2.962c-.866-1.293-1.378-2.84-1.378-4.516 0-4.43 3.6-8.03 8.03-8.03s8.03 3.6 8.03 8.03-3.6 8.03-8.03 8.03zM16 4C9.373 4 4 9.373 4 16c0 2.116.55 4.103 1.51 5.83L4 28l6.36-1.66A11.95 11.95 0 0 0 16 28c6.627 0 12-5.373 12-12S22.627 4 16 4z" />
    </svg>
  );
}
